import React, { useContext } from 'react'

import { Typography } from '@mui/material'
import type { NodeViewProps } from '@tiptap/core'
import { mergeAttributes } from '@tiptap/core'
import Mention from '@tiptap/extension-mention'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { DayObjectSearchEntry } from 'types/graphql'

import { DayContext } from 'src/lib/dayContext'

interface CustomMentionComponentProps extends NodeViewProps {
  node: NodeViewProps['node'] & {
    attrs: {
      id: DayObjectSearchEntry
    }
  }
}

const CustomMentionComponent: React.FC<CustomMentionComponentProps> = ({
  node,
  editor,
  selected,
  HTMLAttributes: viewHTMLAttributes,
}) => {
  const mentionData = node.attrs.searchObject
  const label = mentionData?.label || 'Unknown'

  const mentionStyle = {
    display: 'inline-block',
    backgroundColor: (theme) => theme.palette.secondary.light,
    p: 0.25,
    borderRadius: 0.75,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  }

  const { setSidebarObject } = useContext(DayContext)

  return (
    <NodeViewWrapper
      as="span"
      {...viewHTMLAttributes}
      className={`tiptap-mention ${selected ? 'selected' : ''}`}
      draggable={editor.isEditable}
    >
      <Typography
        variant="body2"
        sx={mentionStyle}
        onClick={() => {
          setSidebarObject({
            objectId: node.attrs.searchObject.object.objectId,
            objectType: node.attrs.searchObject.object.objectType,
            workspaceId: node.attrs.searchObject.object.workspaceId,
          })
        }}
      >
        @{label}
      </Typography>
    </NodeViewWrapper>
  )
}

const TiptapCustomMention = Mention.extend({
  name: 'mention',

  addAttributes() {
    return {
      searchObject: {
        default: null,
        renderHTML: (attributes: { searchObject?: DayObjectSearchEntry }) => {
          if (
            attributes.searchObject?.object?.objectId &&
            attributes.searchObject.label
          ) {
            return {
              'data-objectid': attributes.searchObject.object.objectId,
              'data-objecttype': attributes.searchObject.object.objectType,
              'data-workspaceid': attributes.searchObject.object.workspaceId,
              'data-label': attributes.searchObject.label,
            }
          }
          return {}
        },
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      `@${(node.attrs.searchObject as { label: string } | null)?.label || 'mention'}`,
    ]
  },

  renderText({ node }) {
    const mentionData = node.attrs.searchObject as DayObjectSearchEntry | null
    const char = (this.options.suggestion?.char as string) || '@'
    return `${char}${mentionData?.label || 'mention'}`
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
        getAttrs: (domNode: HTMLElement | string) => {
          if (typeof domNode === 'string') {
            return false
          }

          const objectId = domNode.getAttribute('data-objectid')
          const objectType = domNode.getAttribute('data-objecttype')
          const workspaceId = domNode.getAttribute('data-workspaceid')
          const label = domNode.getAttribute('data-label')

          if (objectId && objectType && workspaceId && label) {
            return {
              searchObject: {
                object: {
                  objectId,
                  objectType,
                  workspaceId,
                  __typename: 'DayObject',
                },
                label,
                __typename: 'DayObjectSearchEntry',
              },
            }
          }
          return false
        },
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CustomMentionComponent)
  },
})

export default TiptapCustomMention
